import React, { useState, useMemo, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { 
  Box
} from '@mui/material';
import { AlertDialog } from '../Common';
import OpenFolderIcon from '../../assets/Open Folder.svg';
import FolderOptionsMenu from './FolderOptionsMenu';
import { dataAPI } from '../../helpers/api_service';

const DocumentFolders = ({ folders = [], remainingMessage = '', onFolderClick, onFolderUpdate, onRenameFolder }) => {
  const { mode } = useThemeMode();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [folderOptionsAnchor, setFolderOptionsAnchor] = useState({});

  const filteredFolders = useMemo(() => {
    return folders || [];
  }, [folders]);

  const handleRename = useCallback((folder) => {
    if (onRenameFolder) {
      onRenameFolder(folder);
    }
  }, [onRenameFolder]);

  const handleDelete = useCallback((folder) => {
    console.log('Delete clicked for folder:', folder);
    setSelectedFolder(folder);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    console.log('Confirming delete for folder:', selectedFolder);
    if (!selectedFolder) return;

    try {
      console.log('Calling deleteFolder API for folder ID:', selectedFolder.id);
      const response = await dataAPI.deleteFolder(selectedFolder.id);
      console.log('Delete folder API response:', response);
      
      if (response.status === 'success') {
        console.log('Folder deleted successfully');
        onFolderUpdate?.();
        setIsDeleteDialogOpen(false);
        setSelectedFolder(null);
      } else {
        console.error('Failed to delete folder:', response.message);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  }, [selectedFolder, onFolderUpdate]);

  const handleOptionsOpen = useCallback((event, folderId) => {
    setFolderOptionsAnchor(prev => ({
      ...prev,
      [folderId]: event.currentTarget
    }));
  }, []);

  const handleOptionsClose = useCallback((folderId) => {
    setFolderOptionsAnchor(prev => ({
      ...prev,
      [folderId]: null
    }));
  }, []);

  if (filteredFolders.length === 0) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <div style={{ color: '#999', fontSize: '16px' }}>
          No folders available
        </div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Remaining Storage/Count Notice */}
      {remainingMessage ? (
        <div style={{
          backgroundColor: 'rgba(38, 195, 192, 0.08)',
          border: '1px solid #26C3C0',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(38, 195, 192, 0.2)'
        }}>
          <span style={{
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            fontSize: '14px',
            fontFamily: 'Urbanist, Arial, sans-serif',
            lineHeight: '1.4'
          }}>
            {remainingMessage}
          </span>
        </div>
      ) : null}
      {/* Folders Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px',
        width: '100%'
      }}>
        {filteredFolders.map((folder) => (
          <div key={folder.id} 
            onClick={() => onFolderClick?.(folder)}
            style={{
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
              borderRadius: '8px',
              padding: '16px',
              border: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = mode === 'dark' 
                ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
                : '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Folder Icon */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              backgroundColor: '#26C3C0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <img 
                src={OpenFolderIcon} 
                alt="Folder"
                style={{
                  width: '16px',
                  height: '16px',
                  filter: 'brightness(0) invert(1)' // Make SVG white
                }}
              />
            </div>

            {/* Folder Info */}
            <div style={{
              flex: 1,
              minWidth: 0
            }}>
              <div style={{
                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Urbanist, Arial, sans-serif',
                marginBottom: '2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {folder.name}
              </div>
              <div style={{
                color: mode === 'dark' ? '#999' : '#666666',
                fontSize: '12px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                {folder.file_count || 0} Files
              </div>
            </div>

            {/* Options Menu */}
            <div 
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px'
              }}
              onClick={(e) => e.stopPropagation()} // Prevent folder navigation when clicking options
            >
              <FolderOptionsMenu
                folder={folder}
                onRename={handleRename}
                onDelete={handleDelete}
                anchorEl={folderOptionsAnchor[folder.id]}
                onClose={() => handleOptionsClose(folder.id)}
                onOpen={(event) => handleOptionsOpen(event, folder.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        type="delete"
        title="Delete Folder"
        message={`Are you sure you want to delete "${selectedFolder?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        confirmButtonColor="#dc3545"
      />
    </Box>
  );
};

export default DocumentFolders;
